import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Globe, Phone, Mail, Clock, Send } from 'lucide-react';

export default function ContactPage() {
  useEffect(() => { document.title = 'Kontakta oss | H.J.Ä.L.P.'; }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));

    toast.success("Meddelande skickat!", {
      description: "Vi återkommer till dig inom 24 timmar.",
    });

    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: Globe,
      title: 'Webbplats',
      content: 'www.sinnerjin.com'
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: '+46 762330966'
    },
    {
      icon: Mail,
      title: 'E-post',
      content: 'George@sinnerjin.com'
    },
    {
      icon: Clock,
      title: 'Öppettider',
      content: 'Alltid öppet'
    }
  ];

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Header Section */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            Kontakta oss
          </h1>
          <p className="text-xl text-muted-foreground">
            Vi är här för att hjälpa dig med frågor om våra KBT-kurser och utbildningar. 
            Kontakta oss så återkommer vi snarast.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold mb-6">
                Kontaktinformation
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {contactInfo.map((info, index) => (
                  <Card key={index} className="shadow-elegant bg-gradient-card border-0">
                    <CardHeader className="pb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-primary rounded-lg flex items-center justify-center">
                          <info.icon className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <CardTitle className="text-lg">{info.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground whitespace-pre-line">
                        {info.content}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Additional Info */}
            <Card className="shadow-elegant bg-gradient-card border-0">
              <CardHeader>
                <CardTitle className="text-xl">Hur kan vi hjälpa dig?</CardTitle>
                <CardDescription>
                  Vi erbjuder support för:
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Frågor om kursinnehåll och struktur</li>
                  <li>• Teknisk support för plattformen</li>
                  <li>• Information om certifiering</li>
                  <li>• Anpassade utbildningslösningar</li>
                  <li>• Företagsutbildningar och grupprabbater</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="shadow-elegant bg-gradient-card border-0">
              <CardHeader>
                <CardTitle className="text-2xl">
                  Skicka ett meddelande
                </CardTitle>
                <CardDescription>
                  Fyll i formuläret nedan så återkommer vi inom 24 timmar.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Namn <span aria-label="obligatoriskt">*</span></Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        aria-required="true"
                        placeholder="Ditt fullständiga namn"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-post <span aria-label="obligatoriskt">*</span></Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        aria-required="true"
                        placeholder="din@email.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Ämne <span aria-label="obligatoriskt">*</span></Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      aria-required="true"
                      placeholder="Vad gäller din fråga?"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Meddelande <span aria-label="obligatoriskt">*</span></Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      aria-required="true"
                      placeholder="Beskriv din fråga eller förfrågan..."
                      rows={5}
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full"
                    size="lg"
                  >
                    {isSubmitting ? (
                      'Skickar...'
                    ) : (
                      <>
                        Skicka meddelande
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mt-16">
          <Card className="shadow-elegant bg-gradient-card border-0">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                Vanliga frågor
              </CardTitle>
              <CardDescription>
                Här är svar på några av de mest frekventa frågorna vi får.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2">Hur lång tid tar kurserna?</h4>
                  <p className="text-muted-foreground text-sm">
                    8-16 veckor, individuell behandlingsplan baserat på programmets RBM-Självskattningsformulär.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Får jag certifikat?</h4>
                  <p className="text-muted-foreground text-sm">
                    Ja, alla som fullföljer sin behandlingsplan erhåller ett officiellt <Link to="/certifikat" className="underline hover:text-foreground transition-colors">certifikat</Link> från H.J.Ä.L.P.-Programmet.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Kan jag studera deltid?</h4>
                  <p className="text-muted-foreground text-sm">
                    Absolut! Våra kurser är flexibla och kan anpassas efter din tidsplan.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Finns grupprabatter?</h4>
                  <p className="text-muted-foreground text-sm">
                    Ja, vi erbjuder specialpriser för företag och större grupper.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}